import { APP_CONSTANTS, STORAGE_KEYS, MESSAGING_API_CONSTANTS, CONVERSATION_CONSTANTS, SUPPORTED_ENTRY_TYPES } from "../helpers/constants";
import { getOrganizationId, getDeploymentDeveloperName, getSalesforceMessagingUrl } from "./dataProvider";
import { getItemInWebStorageByKey, clearWebStorage } from "../helpers/webstorageUtils";
import { util } from "../helpers/common";

/**
 * Build API URL based on environment.
 * In development mode, returns relative paths to leverage setupProxy.js
 * In production/staging, returns full URLs with Salesforce domain
 *
 * @param {String} path - API path like "/iamessage/api/v2/..."
 * @returns {String} - Full URL or relative path depending on environment
 */
function buildApiUrl(path) {
	const isDevelopment = process.env.NODE_ENV === 'development';

	if (isDevelopment) {
		// Development: Use relative path for proxy
		// setupProxy.js will intercept and forward to Salesforce
		return path;
	} else {
		// Production/Staging: Use full URL
		// CORS must be configured in Salesforce to allow your domain
		const messagingUrl = getSalesforceMessagingUrl();
		const fullUrl = `${messagingUrl}${path}`;
		return fullUrl;
	}
}

/**
 * Send an HTTP request using fetch with a specified path, method, mode, headers, and body.
 *
 * @param {String} apiPath - Endpoint to make request to.
 * @param {String} method - HTTP request method (POST, GET, DELETE).
 * @param {String} mode - HTTP mode (cors, no-cors, same-origin, navigate).
 * @param {Object} requestHeaders - Headers to include with request.
 * @param {Object} requestBody - Body to include with request. This method stringifies the object passed in, except when
 *                               uploading a file. For file attachments, request body must be binary data.
 * @returns {Promise}
 */
function sendFetchRequest(apiPath, method, mode, requestHeaders, requestBody) {
	const messagingJwt = getItemInWebStorageByKey(STORAGE_KEYS.JWT);
	const headers = requestHeaders ?
		requestHeaders :
		{
			"Content-Type": "application/json",
			...(messagingJwt && { "Authorization": "Bearer " + messagingJwt })
		};
	const body = requestBody ? JSON.stringify(requestBody) : undefined;

	return fetch(
		apiPath,
		{
			method,
			mode,
			headers,
			...(body && { body })
		}
	).then(async (response) => {
		if (response.status === 401) {
			// Unauthorized request. Clear the web storage.
			clearWebStorage();
		}
		if (!response.ok) {
			let responseObject;
			try {
				responseObject = Object.assign(response, await response.json());
			} catch (e) {
				responseObject = Object.assign(response, {"message": `Error reading the body stream of error object: ${response}`});
			}
			throw responseObject;
		}
		return response;
	});
};

/**
 * Get a JWT for an anonymous user. This JWT is used for unauthenticated conversations.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=generateAccessTokenForUnauthenticatedUser
 *
 * @returns {Promise}
 */
function getUnauthenticatedAccessToken() {
	const orgId = getOrganizationId();
	const esDeveloperName = getDeploymentDeveloperName();
	const messagingUrl = getSalesforceMessagingUrl();
	const capabilitiesVersion = APP_CONSTANTS.APP_CAPABILITIES_VERSION;
	const platform = APP_CONSTANTS.APP_PLATFORM;
	const apiPath = buildApiUrl('/iamessage/api/v2/authorization/unauthenticated/access-token');

	if (!messagingUrl) {
		throw new Error("MESSAGING_URL is not defined in sessionStorage");
	}

	return sendFetchRequest(
		apiPath,
		"POST",
		"cors",
		{
			"Content-Type": "application/json"
		},
		{
			orgId,
			esDeveloperName,
			capabilitiesVersion,
			platform
		}
	).then(response => {
		if (!response.ok) {
			throw response;
		}
		return response.json();
	});
}

/**
 * Create a new conversation.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=createConversation
 *
 * @param {Object} routingAttributes - Optional. Prechat data to be used while routing the conversation request.
 * @returns {Promise}
 */
function createConversation(conversationId, routingAttributes) {
	const esDeveloperName = getDeploymentDeveloperName();
	const apiPath = buildApiUrl('/iamessage/api/v2/conversation');

	return sendFetchRequest(
		apiPath,
		"POST",
		"cors",
		null,
		{
			...(routingAttributes && { routingAttributes }),
			esDeveloperName,
			conversationId
		},
		"createNewConversation"
	).then(response => {
		if (!response.ok) {
			throw response;
		}
	});
}

/**
 * Get a JWT with the same subjectId but new clientId as the existing Messaging JWT that was issued previously. This function is used for session continuity in the same tab (page reload) and/or across tabs.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=generateContinuationToken
 *
 * @returns {Promise}
 */
function getContinuityJwt() {
	const apiPath = buildApiUrl('/iamessage/api/v2/authorization/continuation-access-token');

	return sendFetchRequest(
		apiPath,
		"GET",
		"cors",
		null,
		null
	).then(response => {
		if (!response.ok) {
			throw response;
		}
		return response.json();
	});
}

/**
 * Get a list of all conversations the current subjectId is participating in.
 * Returns:
 * - number of open conversations
 * - number of closed conversations
 * - array of conversations
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=listConversations
 *
 * @param {Boolean} includeClosedConversations - Whether to include closed conversations in list. Optional.
 * @returns {Promise}
 */
function listConversations(includeClosedConversations = false){
	const apiPath = buildApiUrl(`/iamessage/api/v2/conversation/list?inclClosedConvs=${includeClosedConversations}&limit=${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_API_NUM_CONVERSATIONS_LIMIT}`);

	return sendFetchRequest(
		apiPath,
		"GET",
		"cors",
		null,
		null
	).then(response => {
		if (!response.ok) {
			throw response;
		}
		return response.json();
	});
};

/**
 * Get a list of conversation entries for a given conversationId.
 * Returns:
 * - array of conversation entries
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=listConversationEntries
 *
 * @param {String} conversationId - The ID of the conversation to get entries for.
 * @param {Number} startTimestamp - The start time for the window of time being requested. Optional.
 * @param {Number} endTimestamp - The end time for the window of time being requested. Optional.
 * @param {String} direction - Query direction either "FromStart" or "FromEnd" of the conversation. Optional.
 * @returns {Promise}
 */
function listConversationEntries(conversationId, startTimestamp, endTimestamp, direction) {
	const limitUrlQueryParam = `${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_ENTRIES_API_ENTRIES_LIMIT ? `limit=${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_ENTRIES_API_ENTRIES_LIMIT}` : ``}`;
	const startTimestampUrlQueryParam = `${startTimestamp ? `&startTimestamp=${startTimestamp}` : ``}`;
	const endTimestampUrlQueryParam = `${endTimestamp ? `&endTimestamp=${endTimestamp}` : ``}`;
	const directionUrlQueryParam = `${direction ? `&direction=${direction}` : ``}`;
	const entryTypeFilterUrlQueryParam = `${SUPPORTED_ENTRY_TYPES ? `&entryTypeFilter=${SUPPORTED_ENTRY_TYPES}` : ``}`;
	const apiPath = buildApiUrl(`/iamessage/api/v2/conversation/${conversationId}/entries?${limitUrlQueryParam}${startTimestampUrlQueryParam}${endTimestampUrlQueryParam}${directionUrlQueryParam}${entryTypeFilterUrlQueryParam}`);

	return sendFetchRequest(
		apiPath,
		"GET",
		"cors",
		null,
		null
	).then(response => {
		if (!response.ok) {
			throw response;
		}
		return response.json();
	})
	.then(responseJson => {
		// Transform the response to look like a conversation entry received via a server-sent event.
		const transformedData = [];

		if (typeof responseJson !== "object") {
			throw new Error(`Expected to receive JSON response, instead received ${responseJson}.`);
		}
		if (!Array.isArray(responseJson.conversationEntries)) {
			throw new Error(`Expected entries to be an Array, instead was: ${responseJson.conversationEntries}.`);
		}
		responseJson.conversationEntries.forEach(conversationEntry => {
			conversationEntry.entryPayload = JSON.stringify(conversationEntry.entryPayload);
			transformedData.push({
				conversationId,
				conversationEntry
			});
		});

		return transformedData;
	});
}

/*
 * Publish a text message to a conversation.
 * 
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=sendMessage
 * 
 * @param {String} conversationId - ID of conversation to send a text message to.
 * @param {String} text - String content to send to conversation.
 * @param {String} messageId - ID of the conversation entry.
 * @param {String} inReplyToMessageId - ID of message this message is a response for.
 * @param {boolean} isNewMessagingSession - Optional. Whether this message should create a new session. Used by session pre-chat forms.
 * @param {Object} routingAttributes - Optional. Pre-chat data to be used while routing the new session request. Used by session pre-chat forms.
 * @param {String} language - Optional. TODO.
 * @returns {Promise}
 */
function sendTextMessage(conversationId, text, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language) {
	const esDeveloperName = getDeploymentDeveloperName();
	const apiPath = buildApiUrl(`/iamessage/api/v2/conversation/${conversationId}/message`);

	return sendFetchRequest(
		apiPath,
		"POST",
		"cors",
		null,
		{
			message: {
				...(inReplyToMessageId && { inReplyToMessageId }),
				id: messageId,
				messageType: CONVERSATION_CONSTANTS.MessageTypes.STATIC_CONTENT_MESSAGE,
				staticContent: {
					formatType: CONVERSATION_CONSTANTS.FormatTypes.TEXT,
					text
				},
			},
			...(routingAttributes && { routingAttributes }),
			...(isNewMessagingSession && { isNewMessagingSession }),
			esDeveloperName,
			...(language && { language })
		}
	).then(response => {
		if (!response.ok) {
			throw response;
		}
		response.json();
	});
}

/**
 * Send a file attachment to a conversation.
 *
 * Refer: https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=sendFile
 *
 * @param {String} conversationId - ID of conversation to send file to.
 * @param {File} file - File object from input or clipboard.
 * @param {String} messageId - ID of the message entry.
 * @param {String} fileId - ID of the file attachment.
 * @param {String} text - Optional description text for the file.
 * @param {Object} routingAttributes - Optional. Pre-chat data for routing.
 * @param {String} language - Optional. Language code.
 * @returns {Promise}
 */
function sendFile(conversationId, file, messageId, fileId, text, routingAttributes, language) {
	const esDeveloperName = getDeploymentDeveloperName();
	const apiPath = buildApiUrl(`/iamessage/api/v2/conversation/${conversationId}/file`);

	// Create FormData for multipart/form-data request
	const formData = new FormData();

	// Part 1: messageEntry JSON metadata
	const messageEntry = {
		esDeveloperName,
		message: {
			id: messageId,
			fileId: fileId,
			...(text && { text })
		},
		...(routingAttributes && { routingAttributes }),
		...(language && { language })
	};

	// Append messageEntry as Blob with explicit Content-Type
	// This is critical for Salesforce to properly parse the JSON part
	const messageEntryBlob = new Blob(
		[JSON.stringify(messageEntry)], 
		{ type: 'application/json' }
	);
	formData.append('messageEntry', messageEntryBlob);

	// Part 2: fileData binary
	// Append the File directly; FormData preserves its MIME type and name
	formData.append('fileData', file, file.name);

	// Send request with special handling for FormData
	return sendFetchRequestForFile(apiPath, formData);
}

/**
 * Send an HTTP request with FormData (for file uploads).
 * Similar to sendFetchRequest but handles multipart/form-data.
 *
 * IMPORTANT: Do NOT set Content-Type header manually.
 * Browser will automatically set it with correct boundary for multipart/form-data.
 *
 * @param {String} apiPath - Endpoint to make request to.
 * @param {FormData} formData - FormData object with file and metadata.
 * @returns {Promise}
 */
function sendFetchRequestForFile(apiPath, formData) {
	const messagingJwt = getItemInWebStorageByKey(STORAGE_KEYS.JWT);

	const headers = {
		...(messagingJwt && { "Authorization": "Bearer " + messagingJwt })
	};

	return fetch(
		apiPath,
		{
			method: "POST",
			mode: "cors",
			headers,
			body: formData  // FormData directly, not JSON.stringify
		}
	).then(async (response) => {
		if (response.status === 401) {
			// Unauthorized request. Clear the web storage.
			clearWebStorage();
		}
		if (!response.ok) {
			let responseObject;
			try {
				responseObject = Object.assign(response, await response.json());
			} catch (e) {
				responseObject = Object.assign(response, {
					"message": `Error reading the body stream of error object: ${response}`
				});
			}
			
			// Enhanced logging for file upload errors
			console.error('[sendFetchRequestForFile] Request failed:', {
				status: response.status,
				statusText: response.statusText,
				url: response.url,
				error: responseObject
			});
			
			throw responseObject;
		}
		
		// Handle 204 No Content or empty responses
		if (response.status === 204) {
			return response;
		}

		try {
			return await response.json();
		} catch (e) {
			// If the body is empty or not valid JSON, return the raw response
			return response;
		}
	});
}

/**
 * Publish a started/stopped typing indicator entry to a conversation.
 *
 * @param {string} conversationId - ID of conversation to send a typing indicator to.
 * @param {string} typingIndicator - Indicate whether to typing started or stopped indicator.
 * @returns {Promise}
 */
function sendTypingIndicator(conversationId, typingIndicator) {
	const apiPath = buildApiUrl(`/iamessage/api/v2/conversation/${conversationId}/entry`);

    return sendFetchRequest(
        apiPath,
        "POST",
        "cors",
        null,
        {
            entryType: typingIndicator,
            id: util.generateUUID()
        }
    );
};

/**
 * Send a read acknowledgement for a specific message.
 * This notifies Salesforce that the end user has read an agent's message.
 *
 * Refer: https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=sendDeliveryAcknowledgementsOrReadReceipts
 *
 * @param {String} conversationId - ID of the conversation
 * @param {String} messageId - ID of the message that was read
 * @returns {Promise}
 */
function sendReadAcknowledgement(conversationId, messageId) {
    const apiPath = buildApiUrl(`/iamessage/api/v2/conversation/${conversationId}/acknowledge-entries`);

    return sendFetchRequest(
        apiPath,
        "POST",
        "cors",
        null,
        {
            acks: [
                {
                    id: util.generateUUID(),
                    entryType: "Read",
                    conversationEntryId: messageId
                }
            ]
        }
    ).catch((err) => {
        // Non-blocking: Log error but don't throw to avoid breaking the chat flow
        console.error(`Failed to send read acknowledgement for message ${messageId}:`, err);
    });
};

/**
 * Close conversation and clean up JWT:
 * - clear JWT variable on inAppService.
 * - remove JWT from web storage (if web storage is available).
 *
 * This endpoint is typically used for anonymous users.
 *
 * Refer https://developer.salesforce.com/docs/service/messaging-api/references/miaw-api-reference?meta=closeConversation
 *
 * @param {String} conversationId - ID of the conversation to close. Required.
 * @returns {Promise}
 */
function closeConversation(conversationId) {
	const esDeveloperName = getDeploymentDeveloperName();
	const apiPath = buildApiUrl(`/iamessage/api/v2/conversation/${conversationId}?esDeveloperName=${esDeveloperName}`);

    return sendFetchRequest(
        apiPath,
        "DELETE",
        "cors",
        null,
        null
    );
};

export { buildApiUrl, getUnauthenticatedAccessToken, createConversation, getContinuityJwt, listConversations, listConversationEntries, sendTextMessage, sendFile, sendTypingIndicator, sendReadAcknowledgement, closeConversation };